import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Users, 
  MapPin, 
  Calendar, 
  FileText, 
  Settings, 
  BarChart3, 
  Zap, 
  Shield, 
  Crown, 
  CheckCircle2, 
  AlertCircle, 
  TrendingUp, 
  Clock, 
  Euro, 
  Lock,
  RefreshCw,
  Flame
} from 'lucide-react';
import { 
  getCurrentUserCompany, 
  isFeatureEnabled, 
  AVAILABLE_FEATURES,
  fetchLocations,
  fetchInterventionRequests,
  supabase,
  isMagicAdmin,
  type Company,
  type Location,
  type InterventionRequest
} from '../lib/supabase';

interface DashboardCard {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  requiredFeature?: string;
  requiredRole?: string[];
  onClick?: () => void;
}

interface CompanyDashboardProps {
  onSectionChange: (section: string) => void;
  company?: Company | null;
}

export function CompanyDashboard({ onSectionChange, company: propCompany }: CompanyDashboardProps) {
  const [company, setCompany] = useState<Company | null>(propCompany || null);
  const [isLoading, setIsLoading] = useState(!propCompany);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    activeLocations: 0,
    pendingBookings: 0,
    monthlySpend: 0,
    teamMembers: 0
  });
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [isRefreshingStats, setIsRefreshingStats] = useState(false);
  const [isMagicAdminUser, setIsMagicAdminUser] = useState(false);

  useEffect(() => {
    if (!propCompany) {
      loadCompanyData();
    } else {
      loadStats(propCompany);
      loadCurrentUserRole();
      checkMagicAdmin();
    }
  }, [propCompany]);

  const loadCompanyData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const companyData = await getCurrentUserCompany();
      
      if (!companyData) {
        setError('No company data found for your account');
      } else {
        setCompany(companyData);
        await loadStats(companyData);
        await loadCurrentUserRole();
        await checkMagicAdmin();
      }
    } catch (err) {
      setError('Failed to load company data');
      console.error('Error loading company:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const checkMagicAdmin = async () => {
    try {
      const isAdmin = await isMagicAdmin();
      setIsMagicAdminUser(isAdmin);
    } catch (err) {
      console.error('Error checking magic admin status:', err);
    }
  };

  const loadCurrentUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // First, get the user's role_id without nested queries
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role_id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        return;
      }

      if (!profile?.role_id) {
        console.log('No role_id found for user');
        return;
      }

      // Then, get the role name separately
      const { data: role, error: roleError } = await supabase
        .from('roles')
        .select('name')
        .eq('id', profile.role_id)
        .single();

      if (roleError) {
        console.error('Error fetching role:', roleError);
        return;
      }

      if (role?.name) {
        setCurrentUserRole(role.name);
        console.log("Current user role:", role.name);
      }
    } catch (err) {
      console.error('Error getting current user role:', err);
    }
  };

  const loadStats = async (companyData: Company) => {
    try {
      setIsRefreshingStats(true);
      // Load company-specific data
      const [locations, interventionRequests, teamMembersCount] = await Promise.all([
        fetchLocations(companyData.id),
        fetchInterventionRequests(companyData.id),
        fetchTeamMembersCount(companyData.id)
      ]);

      // Calculate stats based on actual data
      const activeLocations = locations.filter(loc => loc.status === 'active').length;
      const pendingBookings = interventionRequests.filter(req => 
        req.status === 'scheduled' || req.status === 'in_progress'
      ).length;
      
      // Calculate monthly spend from intervention requests (current month)
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const monthlySpend = interventionRequests
        .filter(req => {
          const reqDate = new Date(req.created_at);
          return reqDate.getMonth() === currentMonth && reqDate.getFullYear() === currentYear;
        })
        .reduce((total, req) => total + (req.total_cost || 0), 0);

      setStats({
        activeLocations,
        pendingBookings,
        monthlySpend,
        teamMembers: teamMembersCount
      });
    } catch (err) {
      console.error('Error loading stats:', err);
      // Use fallback stats if loading fails
      setStats({
        activeLocations: 0,
        pendingBookings: 0,
        monthlySpend: 0,
        teamMembers: 0
      });
    } finally {
      setIsRefreshingStats(false);
    }
  };

  // Simplified function to get team member count without fetching full profiles
  const fetchTeamMembersCount = async (companyId: string): Promise<number> => {
    try {
      console.log("Fetching team member count for company ID:", companyId);
      
      // Check if current user is magic admin first
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) return 0;
      
      const isMagicAdminUser = await checkIfUserIsMagicAdmin(user.email);
      
      if (isMagicAdminUser) {
        // Magic admin can see all users for the company
        const { count, error } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('is_active', true)
          .not('email', 'in', '("gael@jaimy.be","jan@jaimy.be")'); // Fixed format with proper parentheses and quotes

        if (error) {
          console.error('Error fetching team member count:', error);
          return 0;
        }

        console.log("Team member count:", count || 0);
        return count || 0;
      } else {
        // For non-magic admin users, check if they're company admin
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role_id')
          .eq('id', user.id)
          .single();

        if (profile?.role_id) {
          const { data: role } = await supabase
            .from('roles')
            .select('name')
            .eq('id', profile.role_id)
            .single();

          if (role?.name === 'admin') {
            // Company admin can see all users for their company
            const { count, error } = await supabase
              .from('user_profiles')
              .select('*', { count: 'exact', head: true })
              .eq('company_id', companyId)
              .eq('is_active', true)
              .not('email', 'in', '("gael@jaimy.be","jan@jaimy.be")'); // Fixed format with proper parentheses and quotes

            if (error) {
              console.error('Error fetching team member count:', error);
              return 0;
            }

            return count || 0;
          }
        }

        // For regular users, just return 1 (themselves)
        return 1;
      }
    } catch (err) {
      console.error('Error in fetchTeamMembersCount:', err);
      return 0;
    }
  };
  
  // Helper function to check if user is magic admin
  const checkIfUserIsMagicAdmin = async (email: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('magic_admins')
        .select('id')
        .eq('email', email)
        .eq('is_active', true)
        .maybeSingle();
        
      if (error) {
        console.error('Error checking magic admin status:', error);
        return false;
      }
      
      return !!data;
    } catch (err) {
      console.error('Error in checkIfUserIsMagicAdmin:', err);
      return false;
    }
  };

  // Check if user has required role
  const hasRequiredRole = (requiredRoles?: string[]): boolean => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!currentUserRole) return false;
    return requiredRoles.includes(currentUserRole);
  };

  const handleRefreshStats = async () => {
    if (!company) return;
    await loadStats(company);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">Unable to Load Dashboard</h3>
        <p className="text-red-700 mb-4">{error || 'Company data not found'}</p>
      </div>
    );
  }

  // Define dashboard cards based on company features and industry
  const getDashboardCards = (): DashboardCard[] => {
    const baseCards: DashboardCard[] = [
      {
        id: 'book-service',
        title: 'Book Service',
        description: 'Schedule maintenance and repair services',
        icon: Calendar,
        color: 'from-blue-500 to-blue-600',
        onClick: () => onSectionChange('book-service')
      },
      {
        id: 'locations',
        title: 'Manage Locations',
        description: 'View and manage your properties',
        icon: MapPin,
        color: 'from-green-500 to-green-600',
        requiredFeature: AVAILABLE_FEATURES.LOCATION_MANAGEMENT.key,
        onClick: () => onSectionChange('locations')
      },
      {
        id: 'bookings',
        title: 'View Bookings',
        description: 'Track all service requests and appointments',
        icon: FileText,
        color: 'from-purple-500 to-purple-600',
        onClick: () => onSectionChange('bookings')
      }
    ];

    // Add professional tier features
    if (company.subscription_tier === 'professional' || company.subscription_tier === 'enterprise') {
      baseCards.push(
        {
          id: 'heating-comfort',
          title: 'Maintenance Contracts',
          description: 'Manage ongoing maintenance agreements',
          icon: Flame,
          color: 'from-orange-500 to-orange-600',
          requiredFeature: AVAILABLE_FEATURES.MAINTENANCE_CONTRACTS.key,
          requiredRole: ['admin', 'operational'],
          onClick: () => onSectionChange('heating-comfort')
        },
        {
          id: 'users',
          title: 'User Management',
          description: 'Manage team members and permissions',
          icon: Users,
          color: 'from-indigo-500 to-indigo-600',
          requiredFeature: AVAILABLE_FEATURES.MULTI_USER_MANAGEMENT.key,
          requiredRole: ['admin'],
          onClick: () => onSectionChange('users')
        },
        {
          id: 'reports',
          title: 'Reports & Analytics',
          description: 'View performance metrics and insights',
          icon: BarChart3,
          color: 'from-teal-500 to-teal-600',
          requiredFeature: AVAILABLE_FEATURES.REPORTING_ANALYTICS.key,
          requiredRole: ['admin', 'operational', 'financial'],
          onClick: () => onSectionChange('reports')
        }
      );
    }

    // Add enterprise tier features
    if (company.subscription_tier === 'enterprise') {
      baseCards.push(
        {
          id: 'invoicing',
          title: 'Advanced Invoicing',
          description: 'Complex billing and financial management',
          icon: Euro,
          color: 'from-yellow-500 to-yellow-600',
          requiredFeature: AVAILABLE_FEATURES.ADVANCED_INVOICING.key,
          requiredRole: ['admin', 'financial'],
          onClick: () => onSectionChange('invoicing')
        },
        {
          id: 'api-integration',
          title: 'API Integration',
          description: 'Connect with external systems',
          icon: Zap,
          color: 'from-pink-500 to-pink-600',
          requiredFeature: AVAILABLE_FEATURES.API_INTEGRATION.key,
          requiredRole: ['admin'],
          onClick: () => onSectionChange('integrations')
        }
      );
    }

    // Add settings (admin only)
    baseCards.push({
      id: 'settings',
      title: 'Settings',
      description: 'Configure your account and preferences',
      icon: Settings,
      color: 'from-gray-500 to-gray-600',
      requiredRole: ['admin'],
      onClick: () => onSectionChange('settings')
    });

    // Filter cards based on enabled features and user role
    return baseCards.filter(card => {
      // Check if feature is enabled
      const featureEnabled = !card.requiredFeature || isFeatureEnabled(company, card.requiredFeature);
      // Check if user has required role
      const hasRole = hasRequiredRole(card.requiredRole);
      return featureEnabled && hasRole;
    });
  };

  const dashboardCards = getDashboardCards();

  // Get subscription tier info
  const getSubscriptionInfo = () => {
    switch (company.subscription_tier) {
      case 'basic':
        return {
          name: 'Basic Plan',
          icon: CheckCircle2,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      case 'professional':
        return {
          name: 'Professional Plan',
          icon: TrendingUp,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200'
        };
      case 'enterprise':
        return {
          name: 'Enterprise Plan',
          icon: Crown,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200'
        };
      default:
        return {
          name: 'Unknown Plan',
          icon: AlertCircle,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
    }
  };

  const subscriptionInfo = getSubscriptionInfo();

  return (
    <div className="space-y-6">
      {/* Company Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div 
              className="h-16 w-16 rounded-lg flex items-center justify-center text-white"
              style={{ backgroundColor: company.branding.primary_color }}
            >
              {company.branding.logo_url ? (
                <img 
                  src={company.branding.logo_url} 
                  alt={company.name}
                  className="h-12 w-12 object-contain"
                />
              ) : (
                <Building2 className="h-8 w-8" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
              <p className="text-gray-600 capitalize">{company.industry} • {company.size} company</p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-lg border ${subscriptionInfo.bgColor} ${subscriptionInfo.borderColor}`}>
            <div className="flex items-center gap-2">
              <subscriptionInfo.icon className={`h-5 w-5 ${subscriptionInfo.color}`} />
              <span className={`font-medium ${subscriptionInfo.color}`}>
                {subscriptionInfo.name}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Locations</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeLocations}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MapPin className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Bookings</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingBookings}</p>
            </div>
            <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Monthly Spend</p>
              <p className="text-2xl font-bold text-gray-900">€{stats.monthlySpend.toFixed(2)}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Euro className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Team Members</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-gray-900">{stats.teamMembers}</p>
                <button 
                  onClick={handleRefreshStats}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isRefreshingStats}
                  title="Refresh stats"
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshingStats ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Cards */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dashboardCards.map((card) => (
            <button
              key={card.id}
              onClick={card.onClick}
              className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-6 text-left border border-gray-100 hover:border-jaimy-red"
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg bg-gradient-to-br ${card.color} text-white group-hover:scale-110 transition-transform`}>
                  <card.icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-jaimy-red transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-gray-600 text-sm mt-1">
                    {card.description}
                  </p>
                </div>
              </div>
            </button>
          ))}

          {/* Disabled cards for property managers */}
          {currentUserRole === 'property_manager' && (
            <>
              {!isFeatureEnabled(company, AVAILABLE_FEATURES.MAINTENANCE_CONTRACTS.key) || (
                !dashboardCards.some(item => item.id === 'heating-comfort') && (
                  <div className="group bg-white rounded-lg shadow-sm p-6 text-left border border-gray-100 opacity-50 cursor-not-allowed relative">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                        <Flame className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Maintenance Contracts
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">
                          Manage ongoing maintenance agreements
                        </p>
                      </div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-500 px-4 py-2 rounded-lg bg-gray-100">
                        <Lock className="h-5 w-5" />
                        <span>Contact admin for access</span>
                      </div>
                    </div>
                  </div>
                )
              )}

              {!dashboardCards.some(item => item.id === 'users') && (
                <div className="group bg-white rounded-lg shadow-sm p-6 text-left border border-gray-100 opacity-50 cursor-not-allowed relative">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
                      <Users className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        User Management
                      </h3>
                      <p className="text-gray-600 text-sm mt-1">
                        Manage team members and permissions
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-500 px-4 py-2 rounded-lg bg-gray-100">
                      <Lock className="h-5 w-5" />
                      <span>Contact admin for access</span>
                    </div>
                  </div>
                </div>
              )}

              {!dashboardCards.some(item => item.id === 'settings') && (
                <div className="group bg-white rounded-lg shadow-sm p-6 text-left border border-gray-100 opacity-50 cursor-not-allowed relative">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-gradient-to-br from-gray-500 to-gray-600 text-white">
                      <Settings className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Settings
                      </h3>
                      <p className="text-gray-600 text-sm mt-1">
                        Configure your account and preferences
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-500 px-4 py-2 rounded-lg bg-gray-100">
                      <Lock className="h-5 w-5" />
                      <span>Contact admin for access</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Feature Overview */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.values(AVAILABLE_FEATURES).map((feature) => {
            const enabled = isFeatureEnabled(company, feature.key);
            return (
              <div
                key={feature.key}
                className={`p-4 rounded-lg border ${
                  enabled 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                    enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {enabled ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <AlertCircle className="h-5 w-5" />
                    )}
                  </div>
                  <div>
                    <h4 className={`font-medium ${enabled ? 'text-green-900' : 'text-gray-600'}`}>
                      {feature.name}
                    </h4>
                    <p className={`text-xs ${enabled ? 'text-green-700' : 'text-gray-500'}`}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}